import React from "react";
import { Heading } from "./Text.jsx";

/* Surface card. elevation: flat (border only) | raised (shadow). pad sizes. */
const PAD = { none: 0, sm: 16, md: 20, lg: 24 };

export function Card({
  children,
  elevation = "raised",
  pad = "md",
  radius = "md",
  interactive = false,
  style,
  ...rest
}) {
  const [hover, setHover] = React.useState(false);
  const r = radius === "lg" ? "var(--radius-lg)" : radius === "sm" ? "var(--radius-xs)" : "var(--radius-md)";
  const baseShadow = elevation === "flat" ? "var(--shadow-inset-border)" : "var(--shadow-inset-border), var(--shadow-2)";
  return (
    <div
      onMouseEnter={() => interactive && setHover(true)}
      onMouseLeave={() => interactive && setHover(false)}
      style={{
        background: "var(--surface-card)",
        borderRadius: r,
        padding: PAD[pad] ?? 20,
        boxShadow: hover ? "var(--shadow-inset-border), var(--shadow-3)" : baseShadow,
        transition: "box-shadow var(--dur) var(--ease), transform var(--dur) var(--ease)",
        transform: hover ? "translateY(-1px)" : "none",
        cursor: interactive ? "pointer" : "default",
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

/* Optional header row for a card. */
export function CardHeader({ title, subtitle, action, style, ...rest }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 16,
        marginBottom: 16,
        ...style,
      }}
      {...rest}
    >
      <div>
        {/* Rendered as a real heading so assistive tech and `getByRole('heading')`
            in journey/unit tests resolve the card title (REG-378). */}
        <h3 style={{ margin: 0, fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 16, color: "var(--text-strong)", letterSpacing: "-0.01em" }}>
          {title}
        </h3>
        {subtitle ? (
          <div style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 13, color: "var(--text-muted)", marginTop: 3 }}>
            {subtitle}
          </div>
        ) : null}
      </div>
      {action ? <div style={{ flex: "none" }}>{action}</div> : null}
    </div>
  );
}

/* Titled in-card section — heading, divider, and body with design-system rhythm.
   Replaces raw <h4> + hand-rolled dividers after the global heading reset (REG-378). */
export function CardSection({ title, titleLevel = 4, divider = true, children, style, ...rest }) {
  return (
    <section style={style} {...rest}>
      {title != null ? (
        <Heading level={titleLevel} style={{ margin: "0 0 var(--space-sm)" }}>
          {title}
        </Heading>
      ) : null}
      {divider ? <div className="u-rule-divider-mb" role="presentation" /> : null}
      {children}
    </section>
  );
}

export default Card;
